import { createContext, useContext, useEffect } from 'react';

/**
 * The page's name, on its way up to the topbar.
 *
 * The shell cannot know what a page is called — the page's `<h1>` is the only
 * place that says it — and passing a title through every route element would
 * mean touching twenty-three pages to change the chrome. So `PageHeader`
 * publishes its title here and the shell reads it.
 *
 * The default is a no-op on purpose: `PageHeader` renders on the signed-out
 * pages too, where there is no shell listening, and a context that threw there
 * would make the kit depend on its own chrome.
 */
const PageTitleContext = createContext<((title: string | null) => void) | null>(null);

export function PageTitleProvider({
  publish,
  children,
}: {
  publish: (title: string | null) => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return <PageTitleContext.Provider value={publish}>{children}</PageTitleContext.Provider>;
}

/**
 * Publish this page's title while it is mounted, and withdraw it on the way
 * out, so a page with no string title (the marketing hero, whose `<h1>` is
 * markup) leaves the shell showing its fallback rather than the last page's
 * name.
 */
export function usePublishPageTitle(title: React.ReactNode): void {
  const publish = useContext(PageTitleContext);
  const text = typeof title === 'string' ? title : null;

  useEffect(() => {
    if (!publish) return;
    publish(text);
    return () => publish(null);
  }, [publish, text]);
}
